import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchSubmission, updateSubmission, syncSubmission, addNote, sendDecisionEmail, sendDrawEmail, uploadPofLetter, fetchPofLetter, deletePofLetter, syncAccounting } from '../../api/portal-api.js';
import type {
  SubmissionRecord,
  FormSubmissionRecord,
  ApiSubmissionRecord,
  SubmissionUpdateInput,
  NoteEntry,
} from '@qdf/shared';
import { getLeadSource } from '@qdf/shared';

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: '#1d4ed8', bg: '#dbeafe' },
  in_review: { label: 'In Review', color: '#b45309', bg: '#fef3c7' },
  approved: { label: 'Approved', color: '#047857', bg: '#d1fae5' },
  rejected: { label: 'Rejected', color: '#b91c1c', bg: '#fee2e2' },
  synced: { label: 'Synced', color: '#6d28d9', bg: '#ede9fe' },
  sync_failed: { label: 'Sync Failed', color: '#dc2626', bg: '#fee2e2' },
  funded: { label: 'Funded', color: '#0d9488', bg: '#ccfbf1' },
};

const AUDIT_ICON_MAP: Record<string, { icon: string; label: string; color: string }> = {
  created: { icon: '📥', label: 'Submission Created', color: '#3b82f6' },
  status_changed: { icon: '🔀', label: 'Status Changed', color: '#f59e0b' },
  fields_updated: { icon: '✏️', label: 'Fields Updated', color: '#6366f1' },
  sync_requested: { icon: '📤', label: 'Sync Requested', color: '#8b5cf6' },
  sync_completed: { icon: '✅', label: 'Sync Completed', color: '#059669' },
  sync_failed: { icon: '❌', label: 'Sync Failed', color: '#dc2626' },
  manual_resync: { icon: '🔁', label: 'Manual Resync Triggered', color: '#7c3aed' },
  note_added: { icon: '📝', label: 'Note Added', color: '#0ea5e9' },
  funded: { icon: '💰', label: 'Marked as Funded', color: '#0d9488' },
  email_sent: { icon: '✉️', label: 'Email Notification Sent', color: '#059669' },
  email_failed: { icon: '⚠️', label: 'Email Send Failed', color: '#dc2626' },
  pof_uploaded: { icon: '📎', label: 'Proof of Funds Uploaded', color: '#0369a1' },
  pof_deleted: { icon: '🗑️', label: 'Proof of Funds Removed', color: '#94a3b8' },
};

export function SubmissionDetail() {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: record, isLoading, error } = useQuery<SubmissionRecord>({
    queryKey: ['portal-submission', submissionId],
    queryFn: () => fetchSubmission(submissionId!),
    enabled: !!submissionId,
  });

  const [edits, setEdits] = useState<SubmissionUpdateInput>({});
  const [fundEdits, setFundEdits] = useState<SubmissionUpdateInput>({});
  const [saving, setSaving] = useState(false);
  const [savingFund, setSavingFund] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  // New Western notify-submitter step
  const [recipientInput, setRecipientInput] = useState('');
  const [amendNote, setAmendNote] = useState('');
  const [amendOpen, setAmendOpen] = useState(false);
  const [amendUnlocked, setAmendUnlocked] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (updates: SubmissionUpdateInput) =>
      updateSubmission(submissionId!, updates),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['portal-submissions-all'] });
      if (response?.sync) {
        showSuccess(response.sync.success
          ? 'Changes saved — synced successfully ✅'
          : `Changes saved — sync failed: ${response.sync.error}`);
      } else {
        showSuccess('Changes saved successfully');
      }
      setEdits({});
      setFundEdits({});
      setSaving(false);
      setSavingFund(false);
    },
    onError: () => { setSaving(false); setSavingFund(false); },
  });

  const syncMutation = useMutation({
    mutationFn: () => syncSubmission(submissionId!),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['portal-submissions-all'] });
      showSuccess(response?.success
        ? 'Synced successfully ✅'
        : `Sync failed: ${response?.message || 'Unknown error'}`);
    },
    onError: (err: any) => {
      showSuccess(`Sync failed: ${err?.message || 'Network error'}`);
    },
  });

  const noteMutation = useMutation({
    mutationFn: (text: string) => addNote(submissionId!, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      setNoteText('');
      setAddingNote(false);
    },
    onError: () => setAddingNote(false),
  });

  // NW: persist decision-email recipients (PATCH without status → no side effects)
  const recipientsMutation = useMutation({
    mutationFn: (emails: string[]) => updateSubmission(submissionId!, { notification_emails: emails }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      setRecipientInput('');
    },
  });

  // NW: explicit decision-email send (first send, retry, or amended correction)
  const sendEmailMutation = useMutation({
    mutationFn: (opts?: { amended?: boolean }) => sendDecisionEmail(submissionId!, opts),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['portal-submissions-all'] });
      if (response.sent) {
        showSuccess(`Decision email sent to ${response.recipients?.join(', ') ?? 'submitter'} ✉️`);
        setAmendOpen(false);
        setAmendUnlocked(false);
        setAmendNote('');
      } else if (response.already_sent) {
        showSuccess('Decision email was already sent — no duplicate sent.');
      } else {
        showSuccess(response.message || 'Email not sent');
      }
    },
    onError: (err: any) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      showSuccess(`Email send failed: ${err?.body?.message || err?.message || 'Unknown error'} — use Retry.`);
    },
  });

  // NW: Proof of Funds letter — uploaded here, attached to the approval email
  const pofInputRef = useRef<HTMLInputElement>(null);
  const pofUploadMutation = useMutation({
    mutationFn: (file: File) => uploadPofLetter(submissionId!, file),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      showSuccess(`Proof of Funds letter "${response.pof_letter.file_name}" uploaded 📎`);
    },
    onError: (err: any) => {
      showSuccess(`Upload failed: ${err?.message || 'Unknown error'}`);
    },
  });
  const pofDeleteMutation = useMutation({
    mutationFn: () => deletePofLetter(submissionId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      showSuccess('Proof of Funds letter removed');
    },
    onError: (err: any) => {
      showSuccess(`Remove failed: ${err?.message || 'Unknown error'}`);
    },
  });

  const handlePofFileSelected = (file: File | undefined) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      showSuccess('The Proof of Funds letter must be a PDF file.');
      return;
    }
    if (file.size > 3 * 1024 * 1024) {
      showSuccess('The PDF is too large — maximum size is 3 MB.');
      return;
    }
    pofUploadMutation.mutate(file);
  };

  const handlePofDownload = async () => {
    try {
      const { download_url } = await fetchPofLetter(submissionId!);
      // Content-Disposition: attachment — navigating downloads without leaving the page.
      window.location.href = download_url;
    } catch (err: any) {
      showSuccess(`Download failed: ${err?.message || 'Unknown error'}`);
    }
  };

  // NW Step 4: disbursement fields — saved any number of times (staff often
  // enter half the wires and come back), synced to accounting exactly once.
  const [disbEdits, setDisbEdits] = useState<SubmissionUpdateInput>({});
  const disbSaveMutation = useMutation({
    mutationFn: (updates: SubmissionUpdateInput) => updateSubmission(submissionId!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['portal-submissions-all'] });
      setDisbEdits({});
      showSuccess('Disbursement details saved 💾');
    },
    onError: (err: any) => {
      showSuccess(`Save failed: ${err?.message || 'Unknown error'}`);
    },
  });
  const syncAccountingMutation = useMutation({
    mutationFn: () => syncAccounting(submissionId!),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      if (response.sent) {
        showSuccess(`Synced to accounting (${response.recipients?.join(', ') ?? ''}) 📤`);
      } else if (response.already_sent) {
        showSuccess('This submission was already synced to accounting — no duplicate sent.');
      } else {
        showSuccess(response.message || 'Accounting sync not sent');
      }
    },
    onError: (err: any) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      showSuccess(`Accounting sync failed: ${err?.body?.message || err?.message || 'Unknown error'} — use Retry.`);
    },
  });
  const setDisbEdit = (key: string, value: any) => {
    setDisbEdits(prev => ({ ...prev, [key]: value }));
  };

  // NW: explicit draw-processed send (Step 3, approved submissions only)
  const sendDrawMutation = useMutation({
    mutationFn: () => sendDrawEmail(submissionId!),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      queryClient.invalidateQueries({ queryKey: ['portal-submissions-all'] });
      if (response.sent) {
        showSuccess(`Draw-processed email sent to ${response.recipients?.join(', ') ?? 'submitter'} 💸`);
      } else if (response.already_sent) {
        showSuccess('Draw-processed email was already sent — no duplicate sent.');
      } else {
        showSuccess(response.message || 'Email not sent');
      }
    },
    onError: (err: any) => {
      queryClient.invalidateQueries({ queryKey: ['portal-submission', submissionId] });
      showSuccess(`Email send failed: ${err?.body?.message || err?.message || 'Unknown error'} — use Retry.`);
    },
  });

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 5000);
  };

  const handleSaveStep1 = () => {
    if (Object.keys(edits).length === 0) return;
    setSaving(true);
    updateMutation.mutate(edits);
  };

  // Save fields only — strips status so backend won't trigger an auto-sync
  const handleSaveNoSync = () => {
    const { status, ...fieldsOnly } = edits;
    if (Object.keys(fieldsOnly).length === 0) return;
    setSaving(true);
    updateMutation.mutate(fieldsOnly);
  };

  const handleSaveStep2 = () => {
    const amt = fundEdits.fund_in_amount ?? apiRecord?.fund_in_amount;
    const dt = fundEdits.fund_in_date ?? apiRecord?.fund_in_date;
    if (!amt || !dt) {
      alert('Please enter both the funded amount and funded date.');
      return;
    }
    setSavingFund(true);
    updateMutation.mutate({
      fund_in_amount: amt,
      fund_in_date: dt,
      status: isFunded ? 'funded' : 'funded', // always send funded status
    });
  };

  const setEdit = (key: string, value: any) => {
    setEdits(prev => ({ ...prev, [key]: value }));
  };

  const setFundEdit = (key: string, value: any) => {
    setFundEdits(prev => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return <div style={s.center}><div style={s.spinner} /></div>;
  }

  if (error || !record) {
    return (
      <div style={s.center}>
        <p style={{ color: '#dc2626', fontSize: '15px' }}>Submission not found</p>
        <button onClick={() => navigate('/portal/submissions')} style={s.backBtn}>← Back to Submissions</button>
      </div>
    );
  }

  const isForm = record.submission_type === 'form';
  const isNw = getLeadSource(record) === 'newwestern';
  const formRecord = isForm ? (record as FormSubmissionRecord) : null;
  const apiRecord = !isForm ? (record as ApiSubmissionRecord) : null;
  const currentStatus = edits.status || record.status;
  const statusInfo = STATUS_MAP[record.status] || { label: record.status, color: '#475569', bg: '#f1f5f9' };

  const hasEdits = Object.keys(edits).length > 0;
  const hasFundEdits = Object.keys(fundEdits).length > 0;
  const isActioned = record.status === 'approved' || record.status === 'rejected' || record.status === 'synced' || record.status === 'sync_failed' || record.status === 'funded';
  const isFunded = record.status === 'funded';
  const canShowFunding = !isForm && apiRecord && currentStatus !== 'rejected' && isActioned;

  // NW decision-email state (two-step flow: decide → notify)
  const emailSentAt = formRecord?.decision_email_sent_at;
  const emailError = formRecord?.decision_email_error;
  const emailFailed = !!emailError && !emailSentAt;
  const correctionSentAt = formRecord?.decision_email_correction_sent_at;
  const notificationEmails = formRecord?.notification_emails ?? [];
  // After the email is out, the decision is final unless explicitly amended.
  const nwDecisionLocked = isNw && !!emailSentAt && !amendUnlocked;

  // NW draw-processed email state (Step 3 — approved submissions only)
  const drawSentAt = formRecord?.draw_processed_email_sent_at;
  const drawError = formRecord?.draw_processed_email_error;
  const drawFailed = !!drawError && !drawSentAt;
  // "Approved" for NW display purposes includes 'funded' — completing Step 4
  // transitions the status, and the approved-flow cards must stay visible.
  const nwApproved = isNw && (record.status === 'approved' || record.status === 'funded');

  // NW Step 4 disbursement state
  const accountingSentAt = formRecord?.accounting_email_sent_at;
  const accountingError = formRecord?.accounting_email_error;
  const accountingFailed = !!accountingError && !accountingSentAt;
  const disbMerged = {
    wire_out_date: (disbEdits.wire_out_date ?? formRecord?.wire_out_date) || undefined,
    wire_out_amount: disbEdits.wire_out_amount ?? formRecord?.wire_out_amount,
    wire_in_date: (disbEdits.wire_in_date ?? formRecord?.wire_in_date) || undefined,
    wire_in_amount: disbEdits.wire_in_amount ?? formRecord?.wire_in_amount,
    bs_title_company: (disbEdits.bs_title_company ?? formRecord?.bs_title_company) || undefined,
    fee_amount: disbEdits.fee_amount ?? formRecord?.fee_amount,
  };
  const hasDisbEdits = Object.keys(disbEdits).length > 0;
  const hasSavedDisbData = !!formRecord && (
    formRecord.wire_out_date !== undefined || formRecord.wire_out_amount !== undefined ||
    formRecord.wire_in_date !== undefined || formRecord.wire_in_amount !== undefined ||
    formRecord.bs_title_company !== undefined || formRecord.fee_amount !== undefined
  );

  const handleSaveDisbursement = () => {
    if (!hasDisbEdits) {
      showSuccess('No changes to save — the disbursement details are up to date.');
      return;
    }
    const updates: SubmissionUpdateInput = { ...disbEdits };
    // A complete wire-in means the money landed — mark the record funded
    // (first time only; resaves of other fields keep whatever status it has).
    if (
      disbMerged.wire_in_amount !== undefined &&
      disbMerged.wire_in_date &&
      record!.status !== 'funded'
    ) {
      updates.status = 'funded';
    }
    disbSaveMutation.mutate(updates);
  };

  const handleSyncAccounting = () => {
    if (hasDisbEdits) {
      showSuccess('Save your changes before syncing to accounting.');
      return;
    }
    if (!window.confirm('Sync these disbursement details to accounting? This can only be done ONCE per submission.')) {
      return;
    }
    syncAccountingMutation.mutate();
  };

  // NW Proof of Funds letter — required before the approval email can go out.
  const pofLetter = formRecord?.pof_letter;
  const pofMissingForApproval = nwApproved && !pofLetter;
  // Once the approval email is out its attachment is part of the record —
  // no removing, and replacing only makes sense while amending.
  const pofCanRemove = !!pofLetter && !(nwApproved && !!emailSentAt);
  const pofCanReplace = !!pofLetter && (!emailSentAt || amendUnlocked);

  // Derive progress from audit log + record fields
  const audit = record.audit_log || [];
  const hasDecision = isActioned;
  const hasDecisionSync = audit.some(e => e.action === 'sync_completed' && !(e.details || '').toLowerCase().includes('funded'));
  const hasDecisionSyncFailed = !hasDecisionSync && audit.some(e => e.action === 'sync_failed');
  const hasFundedStep = isFunded;
  const hasFundingSync = isFunded && (record.sync_status === 'synced');
  const hasFundingSyncFailed = isFunded && (record.sync_status === 'failed');

  // Build simplified progress steps — no internal system details.
  // New Western submissions never sync — their third step is the decision
  // email to the submitter.
  const progressSteps: { label: string; icon: string; done: boolean; failed?: boolean; active?: boolean }[] = isNw
    ? [
        { label: 'Received', icon: '📥', done: true },
        { label: currentStatus === 'rejected' ? 'Denied' : 'Reviewed', icon: currentStatus === 'rejected' ? '✕' : '✓', done: hasDecision, failed: currentStatus === 'rejected' },
        { label: 'Email Sent', icon: '✉️', done: !!emailSentAt, failed: emailFailed, active: hasDecision && !emailSentAt && !emailFailed },
        // Approved applications: notify once the draw is processed, then record
        // the disbursement and sync it to accounting (Step 4).
        ...(nwApproved
          ? [
              { label: 'Draw Processed', icon: '💸', done: !!drawSentAt, failed: drawFailed, active: !!emailSentAt && !drawSentAt && !drawFailed },
              { label: 'Disbursed', icon: '💰', done: !!accountingSentAt, failed: accountingFailed, active: !!drawSentAt && !accountingSentAt && !accountingFailed },
            ]
          : []),
      ]
    : isForm
    ? [
        { label: 'Received', icon: '📥', done: true },
        { label: currentStatus === 'rejected' ? 'Denied' : 'Reviewed', icon: currentStatus === 'rejected' ? '✕' : '✓', done: hasDecision, failed: currentStatus === 'rejected' },
        { label: 'Synced', icon: '🔗', done: hasDecisionSync, failed: hasDecisionSyncFailed },
      ]
    : [
        { label: 'Received', icon: '📥', done: true },
        { label: 'Funded Out', icon: '💵', done: hasDecision, failed: currentStatus === 'rejected' },
        { label: 'Synced', icon: '✅', done: hasDecisionSync, failed: hasDecisionSyncFailed, active: hasDecision && !hasDecisionSync && !hasDecisionSyncFailed },
        { label: 'Funded In', icon: '💰', done: hasFundedStep, active: hasDecisionSync && !hasFundedStep },
        { label: 'Synced', icon: '✅', done: hasFundingSync, failed: hasFundingSyncFailed, active: hasFundedStep && !hasFundingSync && !hasFundingSyncFailed },
      ];

  return (
    <div style={s.page}>
      {/* ─── Header ─── */}
      <div style={s.headerRow}>
        <button onClick={() => navigate('/portal/submissions')} style={s.backBtn}>← Back</button>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' as const }}>
            <h1 style={s.title}>
              {isForm ? formRecord!.full_name : apiRecord!.property_address_1}
            </h1>
            <span style={{ ...s.statusBadgeLg, background: statusInfo.bg, color: statusInfo.color }}>
              {statusInfo.label}
            </span>
          </div>
          <div style={s.metaRow}>
            <span style={s.idBadge}>{record.submission_id}</span>
            <span style={s.typeBadge}>{isForm ? '📋 Onboarding Form' : '💰 Disbursement'}</span>
            <span style={{
              padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
              ...(isNw
                ? { background: '#faf6ef', color: '#967448', border: '1px solid #e8dcc8' }
                : { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0' }),
            }}>
              {isNw ? 'New Western' : 'Left Main'}
            </span>
            <span style={s.dateText}>Submitted {new Date(record.submitted_at).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {successMsg && <div style={s.successBanner}>✓ {successMsg}</div>}

      {/* ─── Progress Stepper ─── */}
      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: '16px',
        padding: '20px 24px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0', flexWrap: 'wrap' as const }}>
          {progressSteps.map((step, i, arr) => (
            <div key={`${step.label}-${i}`} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '8px 16px', borderRadius: '24px',
                background: step.failed ? '#fef2f2'
                  : step.done ? 'linear-gradient(135deg, #059669, #10b981)'
                  : step.active ? '#eff6ff'
                  : '#f8fafc',
                border: step.failed ? '2px solid #fca5a5'
                  : step.done ? 'none'
                  : step.active ? '2px solid #93c5fd'
                  : '1px solid #e2e8f0',
                boxShadow: step.done && !step.failed ? '0 2px 8px rgba(5,150,105,0.2)' : 'none',
                transition: 'all 0.3s ease',
              }}>
                <span style={{
                  width: '24px', height: '24px', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700,
                  background: step.failed ? '#fee2e2'
                    : step.done ? 'rgba(255,255,255,0.25)'
                    : step.active ? '#dbeafe'
                    : '#f1f5f9',
                  color: step.failed ? '#dc2626'
                    : step.done ? '#fff'
                    : step.active ? '#2563eb'
                    : '#94a3b8',
                }}>
                  {step.failed ? '✕' : step.done ? '✓' : step.icon}
                </span>
                <span style={{
                  fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' as const,
                  color: step.failed ? '#dc2626'
                    : step.done ? '#fff'
                    : step.active ? '#2563eb'
                    : '#94a3b8',
                }}>
                  {step.label}
                </span>
              </div>
              {i < arr.length - 1 && (
                <div style={{
                  width: '24px', height: '2px', flexShrink: 0,
                  background: step.done && arr[i + 1].done
                    ? '#10b981'
                    : step.done ? 'linear-gradient(90deg, #10b981, #e2e8f0)'
                    : '#e2e8f0',
                }} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ═══ 2-COLUMN GRID ═══ */}
      <div style={s.grid2}>
        {/* ── LEFT: Submission Details + Notes ── */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '16px' }}>
          {/* Submission Details */}
          <div style={s.card}>
            <h2 style={s.cardTitle}>📄 Submission Details</h2>
            {isForm && formRecord && (
              <div style={s.fieldGrid2col}>
                {formRecord.partner_submission_id && <Field label="Partner Ref" value={formRecord.partner_submission_id} />}
                <Field label="Full Name" value={formRecord.full_name} />
                <Field label="Entity Name" value={formRecord.entity_name} />
                <Field label="DBA" value={formRecord.dba} />
                <Field label="Email" value={formRecord.email} />
                <Field label="Phone" value={formRecord.phone} />
                <Field label="Address" value={`${formRecord.mailing_address_1}${formRecord.mailing_address_2 ? ', ' + formRecord.mailing_address_2 : ''}`} />
                <Field label="City, State ZIP" value={`${formRecord.mailing_city}, ${formRecord.mailing_state} ${formRecord.mailing_zip}`} />
                <Field label="LOC Requested" value={formatCurrency(formRecord.loc_amount_requested)} highlight />
                <Field label="Markets Operating" value={formRecord.markets_operating} />
                <Field label="Avg Annual Txns" value={String(formRecord.avg_annual_transactions)} />
                <Field label="Avg Purchase Price" value={formatCurrency(formRecord.avg_purchase_price)} />
                <Field label="Preferred Title Co" value={formRecord.preferred_title_company} />
              </div>
            )}
            {!isForm && apiRecord && (
              <div style={s.fieldGrid2col}>
                <Field label="Partner" value={apiRecord.partner_name} />
                <Field label="Partner Ref" value={apiRecord.partner_submission_id} />
                <Field label="Property Address" value={`${apiRecord.property_address_1}${apiRecord.property_address_2 ? ', ' + apiRecord.property_address_2 : ''}`} />
                <Field label="City, State ZIP" value={`${apiRecord.property_city}, ${apiRecord.property_state} ${apiRecord.property_zip}`} />
                <Field label="Purchase Price" value={formatCurrency(apiRecord.contract_purchase_price)} />
                <Field label="Fund Requested" value={formatCurrency(apiRecord.fund_amount_requested)} highlight />
                <Field label="Est. Closing" value={apiRecord.estimated_closing_date} />
                <Field label="Front Title Co" value={apiRecord.front_title_company_name} />
                <Field label="Front Contact" value={`${apiRecord.front_title_contact_name || ''} ${apiRecord.front_title_contact_email ? `(${apiRecord.front_title_contact_email})` : ''}`} />
                <Field label="Back Title Co" value={apiRecord.back_title_company_name} />
                <Field label="Back Contact" value={`${apiRecord.back_title_contact_name || ''} ${apiRecord.back_title_contact_email ? `(${apiRecord.back_title_contact_email})` : ''}`} />
                {apiRecord.drive_folder_url && <Field label="Documents" value={apiRecord.drive_folder_url} isLink />}
              </div>
            )}
          </div>

          {/* Uploaded Files — New Western only (Proof of Funds letter) */}
          {isNw && formRecord && (
            <div style={s.card}>
              <h2 style={s.cardTitle}>📎 Uploaded Files</h2>
              {pofLetter ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' as const,
                  background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px 14px',
                }}>
                  <span style={{ fontSize: '22px' }}>📄</span>
                  <div style={{ flex: 1, minWidth: '160px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b', wordBreak: 'break-all' as const }}>
                      {pofLetter.file_name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#64748b' }}>
                      Proof of Funds Letter · {formatFileSize(pofLetter.size_bytes)} · uploaded{' '}
                      {new Date(pofLetter.uploaded_at).toLocaleString()} by {formatAuthor(pofLetter.uploaded_by)}
                    </div>
                  </div>
                  <button onClick={handlePofDownload} style={{ ...s.secondaryBtn, marginTop: 0, whiteSpace: 'nowrap' as const }}>
                    ⬇ Download
                  </button>
                </div>
              ) : (
                <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0 }}>
                  No files uploaded yet. The Proof of Funds letter is uploaded in the
                  Notify Submitter step once the application is approved.
                </p>
              )}
            </div>
          )}

          {/* Notes */}
          <div style={s.card}>
            <h2 style={s.cardTitle}>💬 Notes</h2>
            <div style={s.noteInputWrap}>
              <textarea
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add a note..."
                maxLength={2000}
                rows={2}
                style={s.noteTextarea}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && noteText.trim()) {
                    setAddingNote(true);
                    noteMutation.mutate(noteText.trim());
                  }
                }}
              />
              <div style={s.noteInputFooter}>
                <span style={{ fontSize: '10px', color: '#94a3b8' }}>{noteText.length}/2000 · ⌘+Enter</span>
                <button
                  onClick={() => { if (!noteText.trim()) return; setAddingNote(true); noteMutation.mutate(noteText.trim()); }}
                  disabled={addingNote || !noteText.trim()}
                  style={{ ...s.noteSubmitBtn, opacity: addingNote || !noteText.trim() ? 0.5 : 1, cursor: addingNote || !noteText.trim() ? 'not-allowed' : 'pointer' }}
                >
                  {addingNote ? '⏳' : '📨 Post'}
                </button>
              </div>
            </div>
            {(record.notes && record.notes.length > 0) ? (
              <div style={{ marginTop: '10px' }}>
                {[...record.notes].reverse().map((note: NoteEntry, i: number) => (
                  <div key={i} style={s.noteItem}>
                    <div style={s.noteAvatar}>
                      <span style={{ color: '#fff', fontSize: '11px', fontWeight: 700 }}>{getInitials(note.author)}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={s.noteHeader}>
                        <span style={s.noteAuthor}>{formatAuthor(note.author)}</span>
                        <span style={s.noteTime}>{formatRelativeTime(note.created_at)}</span>
                      </div>
                      <div style={s.noteText}>{note.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: '#94a3b8', fontSize: '12px', textAlign: 'center' as const, marginTop: '16px' }}>No notes yet.</p>
            )}
          </div>
        </div>

        {/* ── RIGHT: Step 1 + Step 2 + Activity Log ── */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '16px' }}>
          {/* Step 1: Decision */}
          <div style={{ ...s.card, padding: '18px' }}>
            <h2 style={{ ...s.cardTitle, marginBottom: '12px', fontSize: '14px' }}>⚡ Step 1: Review Decision</h2>

            <div style={s.inputGroup}>
              <label style={s.label}>Decision</label>
              <div style={s.decisionRow}>
                <button
                  onClick={() => setEdit('status', 'approved')}
                  disabled={isFunded || nwDecisionLocked}
                  style={{
                    ...s.decisionBtn,
                    background: currentStatus === 'approved' || currentStatus === 'funded' ? '#d1fae5' : '#fff',
                    border: currentStatus === 'approved' || currentStatus === 'funded' ? '2px solid #059669' : '1px solid #e2e8f0',
                    color: currentStatus === 'approved' || currentStatus === 'funded' ? '#047857' : '#64748b',
                    fontWeight: currentStatus === 'approved' || currentStatus === 'funded' ? 700 : 500,
                    opacity: isFunded || nwDecisionLocked ? 0.6 : 1, cursor: isFunded || nwDecisionLocked ? 'not-allowed' : 'pointer',
                  }}
                >✓ Approve</button>
                <button
                  onClick={() => setEdit('status', 'rejected')}
                  disabled={isFunded || nwDecisionLocked}
                  style={{
                    ...s.decisionBtn,
                    background: currentStatus === 'rejected' ? '#fee2e2' : '#fff',
                    border: currentStatus === 'rejected' ? '2px solid #dc2626' : '1px solid #e2e8f0',
                    color: currentStatus === 'rejected' ? '#b91c1c' : '#64748b',
                    fontWeight: currentStatus === 'rejected' ? 700 : 500,
                    opacity: isFunded || nwDecisionLocked ? 0.6 : 1, cursor: isFunded || nwDecisionLocked ? 'not-allowed' : 'pointer',
                  }}
                >✕ Deny</button>
              </div>
              {record.status === 'new' && !edits.status && (
                <p style={s.decisionHint}>Select a decision above to approve or deny this submission.</p>
              )}
              {isFunded && (
                <p style={s.decisionHint}>This submission has been <strong>funded</strong>. Decision is locked.</p>
              )}
              {nwDecisionLocked && (
                <div style={{ marginTop: '6px' }}>
                  <p style={s.decisionHint}>
                    The decision email was sent on {new Date(emailSentAt!).toLocaleString()} — the decision is locked.
                  </p>
                  {!correctionSentAt && (
                    !amendOpen ? (
                      <button onClick={() => setAmendOpen(true)} style={s.resyncLink}>
                        ✏️ Amend decision…
                      </button>
                    ) : (
                      <div style={{ marginTop: '6px', padding: '10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                        <p style={{ fontSize: '11px', color: '#92400e', margin: '0 0 6px', lineHeight: 1.4 }}>
                          Amending unlocks the decision. A note explaining why is required; you can then send ONE corrected email to the applicant.
                        </p>
                        <textarea
                          value={amendNote}
                          onChange={e => setAmendNote(e.target.value)}
                          placeholder="Why is the decision being amended? (required)"
                          rows={2}
                          style={{ ...s.input, resize: 'none' as const, marginBottom: '6px' }}
                        />
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => {
                              if (!amendNote.trim()) return;
                              noteMutation.mutate(`[DECISION AMENDED] ${amendNote.trim()}`);
                              setAmendUnlocked(true);
                              setAmendOpen(false);
                            }}
                            disabled={!amendNote.trim()}
                            style={{ ...s.secondaryBtn, flex: 1, marginTop: 0, opacity: amendNote.trim() ? 1 : 0.5 }}
                          >
                            Unlock Decision
                          </button>
                          <button
                            onClick={() => { setAmendOpen(false); setAmendNote(''); }}
                            style={{ ...s.secondaryBtn, flex: 1, marginTop: 0 }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            <div style={s.inputGroup}>
              <label style={s.label}>Projected Close Date</label>
              <input type="date" value={edits.projected_close_date ?? record.projected_close_date ?? ''} onChange={e => setEdit('projected_close_date', e.target.value)} style={s.input} />
            </div>

            {isForm && (
              <>
                <div style={s.inputGroup}>
                  {/* On NW records this field IS the front-side title company */}
                  <label style={s.label}>{isNw ? 'FS Title Company' : 'Pref Title Co Name'}</label>
                  <input type="text" value={edits.pref_title_co_name ?? formRecord?.pref_title_co_name ?? ''} onChange={e => setEdit('pref_title_co_name', e.target.value)} style={s.input} placeholder="Enter title company name" />
                </div>
                <div style={s.row}>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Pref Title Co Email</label>
                    <input type="email" value={edits.pref_title_co_email ?? formRecord?.pref_title_co_email ?? ''} onChange={e => setEdit('pref_title_co_email', e.target.value)} style={s.input} />
                  </div>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Pref Title Co Phone</label>
                    <input type="tel" value={edits.pref_title_co_phone ?? formRecord?.pref_title_co_phone ?? ''} onChange={e => setEdit('pref_title_co_phone', e.target.value)} style={s.input} />
                  </div>
                </div>
              </>
            )}

            {!isForm && apiRecord && (
              <>
                <div style={s.row}>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Front Title Phone</label>
                    <input type="tel" value={edits.front_title_contact_phone ?? apiRecord.front_title_contact_phone ?? ''} onChange={e => setEdit('front_title_contact_phone', e.target.value)} style={s.input} />
                  </div>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Back Title Phone</label>
                    <input type="tel" value={edits.back_title_contact_phone ?? apiRecord.back_title_contact_phone ?? ''} onChange={e => setEdit('back_title_contact_phone', e.target.value)} style={s.input} />
                  </div>
                </div>
                <div style={s.row}>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Fund Out Amount ($)</label>
                    <input type="number" value={edits.fund_out_amount ?? apiRecord.fund_out_amount ?? ''} onChange={e => setEdit('fund_out_amount', e.target.value ? Number(e.target.value) : undefined)} style={s.input} />
                  </div>
                  <div style={s.inputGroup}>
                    <label style={s.label}>Fund Out Date</label>
                    <input type="date" value={edits.fund_out_date ?? apiRecord.fund_out_date ?? ''} onChange={e => setEdit('fund_out_date', e.target.value)} style={s.input} />
                  </div>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <button
                onClick={handleSaveStep1}
                disabled={saving || !hasEdits}
                style={{ ...s.primaryBtn, flex: 1, opacity: saving || !hasEdits ? 0.5 : 1, cursor: saving || !hasEdits ? 'not-allowed' : 'pointer' }}
              >
                {saving ? '⏳ Saving...' : isNw ? '💾 Save Decision' : '💾 Save & Sync'}
              </button>
              {hasEdits && !edits.status && (
                <button
                  onClick={handleSaveNoSync}
                  disabled={saving}
                  style={{ ...s.secondaryBtn, flex: 1, opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                >
                  💾 Save Only
                </button>
              )}
            </div>
            {(currentStatus === 'approved' || currentStatus === 'rejected') && (
              <p style={s.autoSyncNote}>
                {isNw
                  ? 'ℹ️ Saving records the decision only. The applicant is NOT emailed until you send the notification in Step 2 below.'
                  : 'ℹ️ "Save & Sync" sends the decision to our partners and updates all tracking systems.'}
              </p>
            )}
            {isActioned && !isNw && (
              <div style={{ marginTop: '10px', padding: '10px 12px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '8px' }}>
                <button
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending}
                  style={{
                    background: '#7c3aed', color: '#fff', border: 'none', borderRadius: '6px',
                    padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: syncMutation.isPending ? 'not-allowed' : 'pointer',
                    opacity: syncMutation.isPending ? 0.6 : 1, width: '100%',
                  }}
                >
                  {syncMutation.isPending ? '⏳ Syncing...' : '🔁 Resync Decision'}
                </button>
                <p style={{ color: '#7c3aed', fontSize: '10px', margin: '6px 0 0', lineHeight: '1.4' }}>
                  Only use if a previous sync failed or the partner team requests another sync.
                </p>
              </div>
            )}
          </div>

          {/* Step 2: Notify Submitter — New Western only, after a decision */}
          {isNw && formRecord && isActioned && (
            <div style={{ ...s.card, padding: '18px', border: emailSentAt ? '2px solid #059669' : emailFailed ? '2px solid #fca5a5' : '2px solid #c0985c' }}>
              <h2 style={{ ...s.cardTitle, marginBottom: '6px', fontSize: '14px' }}>✉️ Step 2: Notify Submitter</h2>
              <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
                <strong>1.</strong> Add anyone else who should receive the decision email.{' '}
                {record.status === 'approved' && (
                  <><strong>2.</strong> Attach the Proof of Funds letter.{' '}</>
                )}
                <strong>{record.status === 'approved' ? '3.' : '2.'}</strong> Send — the applicant and everyone listed get ONE email.
              </p>

              {/* 1 — Recipients */}
              <div style={s.inputGroup}>
                <label style={s.label}>1 · Notification Recipients</label>
                <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: '6px', marginBottom: '8px' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '5px',
                    padding: '4px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: 600,
                    background: '#f0fdf4', color: '#047857', border: '1px solid #bbf7d0',
                  }}>
                    🔒 {formRecord.email}
                    <span style={{ fontSize: '9px', fontWeight: 500, color: '#059669' }}>applicant · always emailed</span>
                  </span>
                  {notificationEmails.map(addr => (
                    <span key={addr} style={{
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      padding: '4px 10px', borderRadius: '14px', fontSize: '12px', fontWeight: 500,
                      background: '#f8fafc', color: '#334155', border: '1px solid #e2e8f0',
                    }}>
                      {addr}
                      {!emailSentAt && (
                        <button
                          onClick={() => recipientsMutation.mutate(notificationEmails.filter(a => a !== addr))}
                          disabled={recipientsMutation.isPending}
                          style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 0, fontSize: '12px', lineHeight: 1 }}
                          title="Remove recipient"
                        >✕</button>
                      )}
                    </span>
                  ))}
                </div>
                {!emailSentAt ? (
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <input
                      type="email"
                      value={recipientInput}
                      onChange={e => setRecipientInput(e.target.value)}
                      placeholder="Add another email to notify…"
                      style={{ ...s.input, flex: 1 }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && recipientInput.trim()) {
                          e.preventDefault();
                          const addr = recipientInput.trim().toLowerCase();
                          if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr) && !notificationEmails.includes(addr)) {
                            recipientsMutation.mutate([...notificationEmails, addr]);
                          }
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const addr = recipientInput.trim().toLowerCase();
                        if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr) && !notificationEmails.includes(addr)) {
                          recipientsMutation.mutate([...notificationEmails, addr]);
                        }
                      }}
                      disabled={recipientsMutation.isPending || !recipientInput.trim()}
                      style={{ ...s.secondaryBtn, marginTop: 0, whiteSpace: 'nowrap' as const, opacity: recipientInput.trim() ? 1 : 0.5 }}
                    >
                      {recipientsMutation.isPending ? '⏳' : '+ Add'}
                    </button>
                  </div>
                ) : (
                  <p style={{ fontSize: '10px', color: '#94a3b8', margin: 0 }}>
                    The email has been sent — recipient changes no longer have any effect.
                  </p>
                )}
              </div>

              {/* 2 — Proof of Funds letter (approved only, attached to the email) */}
              {record.status === 'approved' && (
                <div style={{ ...s.inputGroup, marginTop: '12px' }}>
                  <label style={s.label}>2 · Proof of Funds Letter (PDF)</label>
                  <input
                    ref={pofInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    style={{ display: 'none' }}
                    onChange={e => {
                      handlePofFileSelected(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                  />
                  {pofLetter ? (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' as const,
                      background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '8px 12px',
                    }}>
                      <span style={{ fontSize: '12px', fontWeight: 600, color: '#0369a1', flex: 1, minWidth: '140px', wordBreak: 'break-all' as const }}>
                        📎 {pofLetter.file_name}
                        <span style={{ fontWeight: 400, color: '#64748b', marginLeft: '6px' }}>
                          ({formatFileSize(pofLetter.size_bytes)})
                        </span>
                      </span>
                      <button onClick={handlePofDownload} style={s.pofActionBtn}>⬇ Download</button>
                      {pofCanReplace && (
                        <button
                          onClick={() => pofInputRef.current?.click()}
                          disabled={pofUploadMutation.isPending}
                          style={s.pofActionBtn}
                        >
                          {pofUploadMutation.isPending ? '⏳' : '🔁 Replace'}
                        </button>
                      )}
                      {pofCanRemove && (
                        <button
                          onClick={() => pofDeleteMutation.mutate()}
                          disabled={pofDeleteMutation.isPending}
                          style={{ ...s.pofActionBtn, color: '#b91c1c' }}
                        >
                          {pofDeleteMutation.isPending ? '⏳' : '✕ Remove'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => pofInputRef.current?.click()}
                        disabled={pofUploadMutation.isPending}
                        style={{ ...s.secondaryBtn, width: '100%', marginTop: 0, opacity: pofUploadMutation.isPending ? 0.6 : 1 }}
                      >
                        {pofUploadMutation.isPending ? '⏳ Uploading…' : '📎 Upload Proof of Funds PDF'}
                      </button>
                      <p style={{ fontSize: '10px', color: '#94a3b8', margin: '5px 0 0', lineHeight: 1.4 }}>
                        Attached to the approval email — required before it can be sent. PDF only, max 3 MB.
                      </p>
                    </>
                  )}
                </div>
              )}

              {/* Send */}
              <div style={{ ...s.inputGroup, marginTop: '12px' }}>
                <label style={s.label}>{record.status === 'approved' ? '3' : '2'} · Send Decision Email</label>
                {emailSentAt ? (
                  <>
                    <div style={s.fundedBanner}>
                      ✉️ Sent {record.status === 'rejected' ? '(rejected)' : '(approved)'}
                      <span style={{ fontSize: '11px', color: '#0f766e', marginLeft: '8px' }}>
                        on {new Date(emailSentAt).toLocaleString()}
                      </span>
                    </div>
                    {correctionSentAt && (
                      <p style={{ fontSize: '10px', color: '#94a3b8', margin: '6px 0 0' }}>
                        A corrected email was also sent on {new Date(correctionSentAt).toLocaleString()}.
                      </p>
                    )}
                    {amendUnlocked && !correctionSentAt && (
                      <>
                        <button
                          onClick={() => sendEmailMutation.mutate({ amended: true })}
                          disabled={sendEmailMutation.isPending || !isActioned || pofMissingForApproval}
                          style={{ ...s.primaryBtn, background: '#b45309', marginTop: '8px', opacity: sendEmailMutation.isPending || pofMissingForApproval ? 0.6 : 1 }}
                        >
                          {sendEmailMutation.isPending ? '⏳ Sending…' : '✉️ Send Corrected Decision Email (once)'}
                        </button>
                        {pofMissingForApproval && (
                          <p style={{ fontSize: '10px', color: '#b45309', margin: '5px 0 0' }}>
                            Upload the Proof of Funds letter above before sending the corrected approval email.
                          </p>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {emailFailed && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', marginBottom: '8px' }}>
                        ⚠️ Last send failed: {emailError}
                      </div>
                    )}
                    <button
                      onClick={() => sendEmailMutation.mutate(undefined)}
                      disabled={sendEmailMutation.isPending || pofMissingForApproval}
                      style={{
                        ...s.primaryBtn,
                        background: record.status === 'rejected' ? '#b91c1c' : '#059669',
                        opacity: sendEmailMutation.isPending || pofMissingForApproval ? 0.6 : 1,
                        cursor: sendEmailMutation.isPending || pofMissingForApproval ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {sendEmailMutation.isPending
                        ? '⏳ Sending…'
                        : emailFailed
                          ? '🔁 Retry Sending Decision Email'
                          : `✉️ Send ${record.status === 'rejected' ? 'Rejection' : 'Approval'} Email to ${1 + notificationEmails.length} Recipient${notificationEmails.length ? 's' : ''}`}
                    </button>
                    <p style={{ fontSize: '10px', color: pofMissingForApproval ? '#b45309' : '#94a3b8', margin: '6px 0 0', lineHeight: 1.4 }}>
                      {pofMissingForApproval
                        ? 'Upload the Proof of Funds letter (step 2 above) to enable sending — it is attached to the approval email.'
                        : 'The applicant receives exactly one decision email — this cannot be sent twice.'}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Draw Processed — New Western only, approved + decision email sent */}
          {isNw && formRecord && nwApproved && !!emailSentAt && (
            <div style={{ ...s.card, padding: '18px', border: drawSentAt ? '2px solid #059669' : drawFailed ? '2px solid #fca5a5' : '2px solid #c0985c' }}>
              <h2 style={{ ...s.cardTitle, marginBottom: '6px', fontSize: '14px' }}>💸 Step 3: Draw Processed</h2>
              <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
                Once the draw for this approved application has been processed, notify the
                applicant here. Goes to the same recipients as the decision email
                ({[formRecord.email, ...notificationEmails].join(', ')}).
              </p>
              {drawSentAt ? (
                <div style={s.fundedBanner}>
                  💸 Draw-processed email sent
                  <span style={{ fontSize: '11px', color: '#0f766e', marginLeft: '8px' }}>
                    on {new Date(drawSentAt).toLocaleString()}
                  </span>
                </div>
              ) : (
                <>
                  {drawFailed && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', marginBottom: '8px' }}>
                      ⚠️ Last send failed: {drawError}
                    </div>
                  )}
                  <button
                    onClick={() => sendDrawMutation.mutate()}
                    disabled={sendDrawMutation.isPending}
                    style={{
                      ...s.primaryBtn,
                      background: '#0f766e',
                      opacity: sendDrawMutation.isPending ? 0.6 : 1,
                      cursor: sendDrawMutation.isPending ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {sendDrawMutation.isPending
                      ? '⏳ Sending…'
                      : drawFailed
                        ? '🔁 Retry Sending Draw-Processed Email'
                        : '💸 Send Draw-Processed Email'}
                  </button>
                  <p style={{ fontSize: '10px', color: '#94a3b8', margin: '6px 0 0', lineHeight: 1.4 }}>
                    The applicant receives exactly one draw-processed email — this cannot be sent twice.
                  </p>
                </>
              )}
            </div>
          )}

          {/* Step 4: Disbursement — New Western only, after the draw email.
              Fields save any number of times; accounting sync is ONCE. */}
          {isNw && formRecord && nwApproved && !!drawSentAt && (
            <div style={{ ...s.card, padding: '18px', border: accountingSentAt ? '2px solid #059669' : accountingFailed ? '2px solid #fca5a5' : '2px solid #c0985c' }}>
              <h2 style={{ ...s.cardTitle, marginBottom: '6px', fontSize: '14px' }}>💰 Step 4: Disbursement</h2>
              <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
                Record the wire and fee details. <strong>Save</strong> as often as you need —
                then <strong>Sync to Accounting</strong> exactly once, when the numbers are final.
              </p>

              <div style={s.row}>
                <div style={s.inputGroup}>
                  <label style={s.label}>Wire Out Date</label>
                  <input type="date" value={disbEdits.wire_out_date ?? formRecord.wire_out_date ?? ''} onChange={e => setDisbEdit('wire_out_date', e.target.value)} style={s.input} />
                </div>
                <div style={s.inputGroup}>
                  <label style={s.label}>Wire Out Amount ($)</label>
                  <input type="number" value={disbEdits.wire_out_amount ?? formRecord.wire_out_amount ?? ''} onChange={e => setDisbEdit('wire_out_amount', e.target.value ? Number(e.target.value) : undefined)} style={s.input} />
                </div>
              </div>
              <div style={s.row}>
                <div style={s.inputGroup}>
                  <label style={s.label}>Wire In Date</label>
                  <input type="date" value={disbEdits.wire_in_date ?? formRecord.wire_in_date ?? ''} onChange={e => setDisbEdit('wire_in_date', e.target.value)} style={s.input} />
                </div>
                <div style={s.inputGroup}>
                  <label style={s.label}>Wire In Amount ($)</label>
                  <input type="number" value={disbEdits.wire_in_amount ?? formRecord.wire_in_amount ?? ''} onChange={e => setDisbEdit('wire_in_amount', e.target.value ? Number(e.target.value) : undefined)} style={s.input} />
                </div>
              </div>
              <div style={s.row}>
                <div style={s.inputGroup}>
                  <label style={s.label}>BS Title Company</label>
                  <input type="text" value={disbEdits.bs_title_company ?? formRecord.bs_title_company ?? ''} onChange={e => setDisbEdit('bs_title_company', e.target.value)} style={s.input} placeholder="Back-side title company" />
                </div>
                <div style={s.inputGroup}>
                  <label style={s.label}>Fee Amount ($)</label>
                  <input type="number" value={disbEdits.fee_amount ?? formRecord.fee_amount ?? ''} onChange={e => setDisbEdit('fee_amount', e.target.value ? Number(e.target.value) : undefined)} style={s.input} />
                </div>
              </div>
              <p style={{ fontSize: '10px', margin: '0 0 8px', color: formRecord.pref_title_co_name ? '#94a3b8' : '#b45309' }}>
                FS Title Company (from Step 1):{' '}
                {formRecord.pref_title_co_name
                  ? <strong>{formRecord.pref_title_co_name}</strong>
                  : <strong>⚠ not set — fill it in Step 1 so it reaches accounting</strong>}
              </p>

              <button
                onClick={handleSaveDisbursement}
                disabled={disbSaveMutation.isPending}
                style={{ ...s.primaryBtn, opacity: disbSaveMutation.isPending ? 0.5 : 1, cursor: disbSaveMutation.isPending ? 'not-allowed' : 'pointer' }}
              >
                {disbSaveMutation.isPending ? '⏳ Saving…' : '💾 Save Disbursement Details'}
              </button>

              <div style={{ ...s.inputGroup, marginTop: '12px' }}>
                {accountingSentAt ? (
                  <div style={s.fundedBanner}>
                    📤 Synced to accounting
                    <span style={{ fontSize: '11px', color: '#0f766e', marginLeft: '8px' }}>
                      on {new Date(accountingSentAt).toLocaleString()}
                    </span>
                  </div>
                ) : (
                  <>
                    {accountingFailed && (
                      <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c', padding: '8px 12px', borderRadius: '8px', fontSize: '12px', marginBottom: '8px' }}>
                        ⚠️ Last sync failed: {accountingError}
                      </div>
                    )}
                    <button
                      onClick={handleSyncAccounting}
                      disabled={syncAccountingMutation.isPending || !hasSavedDisbData || hasDisbEdits}
                      style={{
                        ...s.primaryBtn,
                        background: '#0369a1',
                        opacity: syncAccountingMutation.isPending || !hasSavedDisbData || hasDisbEdits ? 0.5 : 1,
                        cursor: syncAccountingMutation.isPending || !hasSavedDisbData || hasDisbEdits ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {syncAccountingMutation.isPending
                        ? '⏳ Syncing…'
                        : accountingFailed
                          ? '🔁 Retry Sync to Accounting'
                          : '📤 Sync to Accounting (once)'}
                    </button>
                    <p style={{ fontSize: '10px', color: '#94a3b8', margin: '6px 0 0', lineHeight: 1.4 }}>
                      {hasDisbEdits
                        ? 'Save your changes first — the sync sends the SAVED values.'
                        : !hasSavedDisbData
                          ? 'Save the disbursement details to enable the accounting sync.'
                          : 'Sends the saved details to accounting. This can only be done once per submission.'}
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Funding — API only, after approval */}
          {canShowFunding && (
            <div style={{ ...s.card, padding: '18px', border: isFunded ? '2px solid #14b8a6' : '2px solid #e2e8f0' }}>
              <h2 style={{ ...s.cardTitle, marginBottom: '12px', fontSize: '14px' }}>💰 Step 2: Funding</h2>

              <div style={s.row}>
                <div style={s.inputGroup}>
                  <label style={s.label}>Funded In Amount ($) *</label>
                  <input
                    type="number"
                    value={fundEdits.fund_in_amount ?? apiRecord!.fund_in_amount ?? ''}
                    onChange={e => setFundEdit('fund_in_amount', e.target.value ? Number(e.target.value) : undefined)}
                    style={s.input}
                  />
                </div>
                <div style={s.inputGroup}>
                  <label style={s.label}>Funded Date *</label>
                  <input
                    type="date"
                    value={fundEdits.fund_in_date ?? apiRecord!.fund_in_date ?? ''}
                    onChange={e => setFundEdit('fund_in_date', e.target.value)}
                    style={s.input}
                  />
                </div>
              </div>

              {isFunded && (
                <div style={s.fundedBanner}>
                  ✅ Funded
                  {(record as any).funded_at && (
                    <span style={{ fontSize: '11px', color: '#0f766e', marginLeft: '8px' }}>
                      on {new Date((record as any).funded_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                onClick={handleSaveStep2}
                disabled={savingFund || updateMutation.isPending}
                style={{
                  ...s.fundedBtn, flex: 1,
                  opacity: savingFund || updateMutation.isPending ? 0.5 : 1,
                  cursor: savingFund || updateMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {savingFund || updateMutation.isPending
                  ? '⏳ Syncing...'
                  : isFunded
                    ? '💾 Save & Resync Funding'
                    : '💰 Mark as Funded & Sync'}
              </button>
              {isFunded && hasFundEdits && (
                <button
                  onClick={() => {
                    const amt = fundEdits.fund_in_amount ?? apiRecord!.fund_in_amount;
                    const dt = fundEdits.fund_in_date ?? apiRecord!.fund_in_date;
                    if (!amt || !dt) return;
                    setSavingFund(true);
                    updateMutation.mutate({ fund_in_amount: amt, fund_in_date: dt });
                  }}
                  disabled={savingFund}
                  style={{ ...s.secondaryBtn, flex: 1, opacity: savingFund ? 0.5 : 1, cursor: savingFund ? 'not-allowed' : 'pointer' }}
                >
                  💾 Save Only
                </button>
              )}
              </div>
            {isFunded && (
              <div style={{ marginTop: '10px', padding: '10px 12px', background: '#f0fdfa', border: '1px solid #99f6e4', borderRadius: '8px' }}>
                <button
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending}
                  style={{
                    background: '#0d9488', color: '#fff', border: 'none', borderRadius: '6px',
                    padding: '7px 14px', fontSize: '12px', fontWeight: 600, cursor: syncMutation.isPending ? 'not-allowed' : 'pointer',
                    opacity: syncMutation.isPending ? 0.6 : 1, width: '100%',
                  }}
                >
                  {syncMutation.isPending ? '⏳ Syncing...' : '🔁 Resync Funding'}
                </button>
                <p style={{ color: '#0d9488', fontSize: '10px', margin: '6px 0 0', lineHeight: '1.4' }}>
                  Only use if a previous sync failed or the partner team requests another sync.
                </p>
              </div>
            )}
            </div>
          )}

          {/* Activity Log */}
          <div style={{ ...s.card, padding: '18px' }}>
            <h2 style={{ ...s.cardTitle, marginBottom: '10px', fontSize: '14px' }}>📜 Activity Log</h2>
            <div style={{ maxHeight: '300px', overflowY: 'auto' as const }}>
              {(record.audit_log && record.audit_log.length > 0) ? (
                [...record.audit_log].reverse().map((entry, i, arr) => {
                  const info = AUDIT_ICON_MAP[entry.action] || { icon: '📌', label: entry.action, color: '#94a3b8' };
                  return (
                    <div key={i} style={{ display: 'flex', gap: '10px', fontSize: '12px' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' as const, alignItems: 'center', width: '14px', flexShrink: 0 }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: info.color, marginTop: '4px' }} />
                        {i < arr.length - 1 && <div style={{ width: '2px', flex: 1, background: '#e2e8f0' }} />}
                      </div>
                      <div style={{ paddingBottom: '10px', flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '6px' }}>
                          <span style={{ fontWeight: 600, color: '#1e293b' }}>{info.icon} {info.label}</span>
                          <span style={{ color: '#94a3b8', fontSize: '10px', whiteSpace: 'nowrap' as const }}>
                            {new Date(entry.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        {entry.user && <div style={{ color: '#64748b', fontSize: '11px' }}>by {entry.user}</div>}
                        {entry.details && <div style={{ color: '#64748b', marginTop: '1px', fontSize: '11px' }}>{entry.details}</div>}
                      </div>
                    </div>
                  );
                })
              ) : (
                <p style={{ color: '#94a3b8', fontSize: '12px' }}>No activity recorded yet.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function Field({ label, value, isLink, highlight }: { label: string; value?: string; isLink?: boolean; highlight?: boolean }) {
  return (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ color: '#64748b', fontSize: '10px', fontWeight: 600, marginBottom: '2px', textTransform: 'uppercase' as const, letterSpacing: '0.4px' }}>
        {label}
      </div>
      {isLink && value ? (
        <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#4338ca', fontSize: '13px', fontWeight: 500 }}>
          Open in Drive ↗
        </a>
      ) : (
        <div style={{ color: '#1e293b', fontSize: '13px', fontWeight: highlight ? 600 : 400, ...(highlight ? { color: '#059669', fontSize: '15px' } : {}) }}>
          {value || '—'}
        </div>
      )}
    </div>
  );
}

function formatCurrency(amount?: number): string {
  if (!amount) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function getInitials(email: string): string {
  const name = email.split('@')[0];
  const parts = name.split(/[._-]/);
  return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
}

function formatAuthor(email: string): string {
  const name = email.split('@')[0];
  return name.split(/[._-]/).map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

function formatRelativeTime(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString();
}

// ─── Styles ─────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: '1340px', margin: '0 auto', padding: '24px 32px', fontFamily: "'Inter', -apple-system, sans-serif" },
  center: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' },
  headerRow: { display: 'flex', gap: '16px', alignItems: 'flex-start', marginBottom: '14px' },
  title: { fontSize: '20px', fontWeight: 700, color: '#0f172a', margin: 0 },
  metaRow: { display: 'flex', flexWrap: 'wrap' as const, gap: '10px', alignItems: 'center', marginTop: '5px' },
  idBadge: { background: '#eef2ff', color: '#4338ca', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, fontFamily: 'monospace' },
  typeBadge: { color: '#64748b', fontSize: '12px' },
  dateText: { color: '#94a3b8', fontSize: '12px' },
  statusBadgeLg: { padding: '3px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 600 },
  backBtn: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', color: '#475569', cursor: 'pointer', whiteSpace: 'nowrap' as const },
  successBanner: { background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', padding: '10px 16px', borderRadius: '10px', marginBottom: '14px', fontSize: '13px', fontWeight: 500 },
  progressBar: { display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '10px 16px', marginBottom: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 380px', gap: '16px', alignItems: 'start' },
  card: { background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9' },
  cardTitle: { fontSize: '15px', fontWeight: 600, color: '#0f172a', margin: '0 0 14px' },
  fieldGrid2col: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' },
  inputGroup: { marginBottom: '10px', flex: 1 },
  label: { display: 'block', color: '#64748b', fontSize: '10px', fontWeight: 600, marginBottom: '3px', textTransform: 'uppercase' as const, letterSpacing: '0.3px' },
  input: { width: '100%', padding: '7px 10px', borderRadius: '6px', fontSize: '13px', border: '1px solid #e2e8f0', outline: 'none', boxSizing: 'border-box' as const },
  row: { display: 'flex', gap: '10px' },
  decisionRow: { display: 'flex', gap: '8px' },
  decisionBtn: { flex: 1, padding: '10px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s', textAlign: 'center' as const },
  decisionHint: { color: '#94a3b8', fontSize: '11px', margin: '5px 0 0' },
  primaryBtn: { width: '100%', background: '#4338ca', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', marginTop: '4px' },
  secondaryBtn: { background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '9px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginTop: '4px' },
  resyncLink: { background: 'none', border: 'none', color: '#7c3aed', fontSize: '11px', fontWeight: 600, cursor: 'pointer', padding: '2px 0', textAlign: 'left' as const },
  pofActionBtn: { background: '#fff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  autoSyncNote: { color: '#4338ca', background: '#eef2ff', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', marginTop: '6px' },
  fundedBanner: { background: '#f0fdfa', border: '1px solid #99f6e4', color: '#0f766e', padding: '8px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600 },
  fundedBtn: { width: '100%', background: 'linear-gradient(135deg, #0d9488, #14b8a6)', color: '#fff', border: 'none', borderRadius: '8px', padding: '9px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' },
  noteInputWrap: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '10px' },
  noteTextarea: { width: '100%', background: 'transparent', border: 'none', outline: 'none', fontSize: '13px', lineHeight: '1.5', resize: 'none' as const, boxSizing: 'border-box' as const },
  noteInputFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', paddingTop: '8px', marginTop: '6px' },
  noteSubmitBtn: { background: '#4338ca', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 12px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' },
  noteItem: { display: 'flex', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f1f5f9' },
  noteAvatar: { width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg, #7c3aed, #a78bfa)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  noteHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  noteAuthor: { fontSize: '12px', fontWeight: 600, color: '#1e293b' },
  noteTime: { fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' as const },
  noteText: { fontSize: '12px', lineHeight: '1.5', color: '#334155', whiteSpace: 'pre-wrap' as const, marginTop: '2px' },
  spinner: { width: '32px', height: '32px', border: '3px solid #e2e8f0', borderTopColor: '#4338ca', borderRadius: '50%', animation: 'spin 0.6s linear infinite' },
};
